import React, { useCallback, useMemo, useState } from "react";
import { CustomSelect } from "../forms/CustomSelect/CustomSelect";
import {
    CustomSelectDefaultTrigger,
    CustomSelectDefaultTriggerProps,
} from "../forms/CustomSelect/CustomSelectDefaultTrigger";
import { Account } from "../../common/types/domains/accounting/account";
import { useWorkspaceAccounts } from "../../api/account.api";
import { EntityIconList } from "../general/EntityIconList/EntityIconList";
import { useEntities } from "../../hooks/useEntities";
import styles from "./AccountSelect.module.scss";

interface Props {
    selectedAccountName?: string;
    onChange: (account: (Account & { entityIds: number[] }) | null) => void;
    dropdownKey: string;
    size?: CustomSelectDefaultTriggerProps["size"];
    className?: string;
    children?: React.ReactNode;
    forTransactionEditing?: boolean;
    entityId: number;
}

export const MultiEntityAccountSelect: React.FC<Props> = ({
    selectedAccountName,
    onChange,
    size,
    className,
    children,
    forTransactionEditing,
    entityId,
    ...customSelectProps
}) => {
    const [search, setSearch] = useState<string>();
    const [popoverVisible, setPopoverVisible] = useState(false);
    const entities = useEntities();
    const { data: accounts, isLoading } = useWorkspaceAccounts();

    const accountsByName: Map<string, Account & { entityIds: number[] }> =
        useMemo(() => {
            if (!accounts) {
                return new Map();
            }

            const map = new Map<string, Account & { entityIds: number[] }>();

            for (const account of accounts) {
                if (
                    account.financialAccountId ||
                    account.code === 10000 // we need to filter our "Cash on Hand" as well
                ) {
                    continue;
                }

                if (map.has(account.name)) {
                    map.get(account.name)!.entityIds.push(
                        Number(account.entityId),
                    );
                } else {
                    map.set(account.name, {
                        ...account,
                        entityIds: [Number(account.entityId)],
                    });
                }
            }

            return map;
        }, [accounts]);

    const enrichedAccounts = useMemo(
        () => [...accountsByName.values()],
        [accountsByName],
    );

    const options = useMemo(
        () =>
            enrichedAccounts
                ?.filter((c) =>
                    c.name.toLowerCase().includes(search?.toLowerCase() ?? ""),
                )
                .map((c) => ({
                    value: c.name ?? "",
                    label: (
                        <div className="d-flex align-items-start justify-content-between">
                            <span>{c.name}</span>
                            <EntityIconList
                                entities={entities.filter((e) =>
                                    c.entityIds.includes(e.id),
                                )}
                                maxEntitiesToDisplay={2}
                                showLabel={false}
                            />
                        </div>
                    ),
                    className: styles.accountWithEntitiesOption,
                })) ?? [],
        [enrichedAccounts, search, entities],
    );

    const onSelected = useCallback(
        (accountName: string) => {
            const account = enrichedAccounts?.find(
                (c) => c.name === accountName,
            );
            if (account) {
                onChange(account);
            }
        },
        [onChange, enrichedAccounts],
    );

    const onShowPopover = useCallback(() => {
        setPopoverVisible(true);
    }, []);

    const onHidePopover = useCallback(() => {
        setSearch(undefined);
        setPopoverVisible(false);
    }, []);

    return (
        <CustomSelect
            onSelected={onSelected}
            options={options}
            loadingOptions={isLoading}
            onSearch={setSearch}
            searchable
            externalSearch
            value={selectedAccountName}
            placeholder={`Select account...`}
            onShowPopover={onShowPopover}
            onHidePopover={onHidePopover}
            className={className}
            dropdownClassName={styles.dropdown}
            showPopover={popoverVisible}
            onDeselect={() => onChange(null)}
            {...customSelectProps}
        >
            {children ?? (
                <CustomSelectDefaultTrigger
                    value={selectedAccountName ?? `Select account...`}
                    size={size}
                />
            )}
        </CustomSelect>
    );
};
